import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { submitSchoolRoster, schoolRoster, errorMessage, ssoToken, whoAmI, getCountries } from "./api";
import { PhoneInput } from "./ui";

const inputCls =
  "w-full rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-forest-600/40 focus:border-forest-600";

// School set-up: the school and its principal only. Teachers and students are added afterwards from
// the principal's own Teachers and Students pages, so they are not part of this form.
export default function SchoolRoster() {
  const [school, setSchool] = useState({ school_name: "", contact_email: "", contact_phone: "", city_country: "", address: "", student_count: "" });
  const [principal, setPrincipal] = useState({ principal_name: "", principal_email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // submission result: { email_to, whatsapp }
  const [signedIn, setSignedIn] = useState(false);
  const [session, setSession] = useState(null); // { token, user } when a principal is signed in
  const [countries, setCountries] = useState([]);
  const [callingCode, setCallingCode] = useState("");

  // Coming straight from sign-up there is a session: fill in the principal's details from it.
  useEffect(() => {
    ssoToken().then((t) => whoAmI(t).then((u) => ({ t, u }))).then(({ t, u }) => {
      setSignedIn(true);
      setSession({ token: t, user: u });
      setPrincipal({ principal_name: u.name || "", principal_email: u.email || "" });
    }).catch(() => {});
    getCountries().then(setCountries).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!callingCode) return toast.error("Choose your country code");
    if (!/^\d{4,10}$/.test(school.contact_phone)) return toast.error("School phone must be 4-10 digits");
    setBusy(true);
    try {
      const body = {
        ...school, contact_calling_code: callingCode, student_count: parseInt(school.student_count, 10), ...principal,
        teachers: [],
        students: [],
      };
      // A signed-in principal sets up their own school; anyone else (the shared link) sends the details
      // for an admin to review.
      const res = session?.user?.account_kind === "organisation" ? await schoolRoster(session.token, body) : await submitSchoolRoster(body);
      setDone(res);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't submit the roster"));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen geo-bg grid place-items-center p-6" data-testid="roster-done">
        <div className="max-w-md text-center">
          <h1 className="font-display font-semibold text-3xl text-forest-700">JazakAllah khair</h1>
          <p className="mt-3 text-foreground/70" data-testid="done-text">
            {signedIn ? "Your school is set up. Add your teachers and students from the Teachers and Students pages in your dashboard." : "We've received your school's details. Our team will review them and be in touch."}</p>
          <ul className="mt-4 text-sm text-foreground/80 space-y-1">
            <li data-testid="done-email">A welcome email is on its way to {done.email_to}.</li>
            {done.whatsapp && <li data-testid="done-whatsapp">We&apos;re also sending you a welcome message on WhatsApp.</li>}
          </ul>
          <Link to={signedIn ? "/dashboard" : "/login"} className="mt-6 inline-block btn-primary">{signedIn ? "Go to your dashboard" : "Back to sign in"} <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    );
  }

  const sf = (k) => ({ value: school[k], onChange: (e) => setSchool((s) => ({ ...s, [k]: k === "contact_phone" ? e.target.value.replace(/\D/g, "") : e.target.value })) });
  return (
    <div className="min-h-screen geo-bg p-4 md:p-10">
      <form onSubmit={submit} className="max-w-3xl mx-auto space-y-8" data-testid="roster-form">
        <div>
          <h1 className="font-display font-semibold text-3xl text-forest-700">Set up your school</h1>
          <p className="mt-2 text-foreground/70">Share your school&apos;s details and its principal. You&apos;ll add teachers and students afterwards from your dashboard.</p>
          {signedIn && <Link to="/dashboard" data-testid="skip-setup" className="mt-2 inline-block text-sm text-forest-700 hover:underline">Skip for now — I&apos;ll do this later</Link>}
        </div>

        <section className="space-y-3">
          <h2 className="font-display font-semibold text-forest-700">School</h2>
          <input required className={inputCls} placeholder="School name" {...sf("school_name")} data-testid="rs-school-name" />
          <div className="grid sm:grid-cols-2 gap-3">
            <input required type="email" className={inputCls} placeholder="School email" {...sf("contact_email")} />
            <div className="flex gap-2">
              <PhoneInput countries={countries} code={callingCode} onCode={setCallingCode} number={school.contact_phone} onNumber={(v) => setSchool((s) => ({ ...s, contact_phone: v }))} maxLength={10} inputClass={inputCls} testId="rs" />
            </div>
            <input required className={inputCls} placeholder="City, country" {...sf("city_country")} />
            <input required type="number" min={1} className={inputCls} placeholder="Estimated no. of students" {...sf("student_count")} />
          </div>
          <input required className={inputCls} placeholder="Address" {...sf("address")} />
        </section>

        <section className="space-y-3">
          <h2 className="font-display font-semibold text-forest-700">Principal</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <input required className={inputCls} placeholder="Principal name" value={principal.principal_name} onChange={(e) => setPrincipal((p) => ({ ...p, principal_name: e.target.value }))} />
            <input required type="email" className={inputCls} placeholder="Principal email" value={principal.principal_email} onChange={(e) => setPrincipal((p) => ({ ...p, principal_email: e.target.value }))} />
          </div>
        </section>

        <button type="submit" disabled={busy} data-testid="roster-submit" className="btn-primary disabled:opacity-60">
          {busy ? "Submitting…" : "Save school"} <ArrowRight className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
